import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { LogMessage } from '../models/log-message.model';
import { CapturedPayload } from '../transporters';
import { ConsoleTransporter } from '../transporters/console-transporter.service';
import { Transporter, TransporterToken } from '../transporters/transporter.model';
import { LogTypes } from '../utils/log-types.model';
import { queueAsMacrotask } from '../utils/macrotasker';
import { LogLevel } from './log-levels.enum';
import { LoggerConfiguration, LoggerConfigurationToken } from './logger-config.model';
import { CbreLogger } from './logger.model';

/**
 * Default Logger implementation
 */
@Injectable()
export class Logger<T = LogMessage> implements CbreLogger {
  private readonly transporters?: Array<Transporter<T>>;

  constructor(
    @Inject(LoggerConfigurationToken) private loggerConfig: LoggerConfiguration,
    @Optional() @Inject(TransporterToken) transporters?: Array<Transporter<T>>
  ) {
    /*
     * ensure array is received, if no received it means only one
     * transporter has been defined and was not configured as multi
     */
    this.transporters = (Array.isArray(transporters) ? transporters : [transporters]).filter(Boolean);
    // Check the NORMALISED list, not the raw injected param. `transporters` is `null` whenever
    // TransporterToken is not provided — which is the entire point of the @Optional() above — so
    // reading `transporters.length` here threw "Cannot read properties of null (reading 'length')"
    // and made the logger unconstructable. The host masked it by always providing the token; every
    // standalone remote shell (nx serve <remote>) hit it at bootstrap and rendered a blank page.
    if (!this.transporters.length) {
      this.transporters = [new ConsoleTransporter()];
    }

    /*
     * convert debug actions as a dictionary of events with their respective configured log level
     * if no level given then it's global level is used
     */
    if (this.loggerConfig.debugActions != null) {
    }
  }

  /**
   * Logs a info message
   * @param message
   */
  info(message: string | T): void {
    this.logWith(LogLevel.Info, message);
  }

  /**
   * Logs debug action messages
   * @param message
   */
  debug(message: string | T): void {
    this.logWith(LogLevel.Debug, message);
  }

  /**
   * Logs warning level messages
   * @param message
   */
  warn(message: string | T): void {
    this.logWith(LogLevel.Warn, message);
  }

  /**
   * Logs error messages
   * @param error
   */
  error(error: string | Error | HttpErrorResponse | T): void {
    this.logWith(LogLevel.Error, error);
  }

  /**
   * If payload evaluates as should be logged then a macro-task is scheduled
   * @param logLevel
   * @param payload
   */
  private logWith(logLevel: LogLevel, payload: LogTypes) {
    // capture payload, timestamp and level
    const capturedPayload: CapturedPayload = { payload, level: logLevel, timestamp: new Date() };
    queueAsMacrotask(() => {
      for (const transport of this.transporters) {
        this.execute(transport, capturedPayload, logLevel);
      }
    });
  }

  /**
   * Queues the logging event as a micro-task
   * @param transport
   * @param payload
   * @param level
   */
  private execute(transport: Transporter<T>, capturedPayload: CapturedPayload, level: LogLevel) {
    queueMicrotask(() => transport.write(transport.template(capturedPayload), level));
  }
}
